import type { MockRecord } from './database.ts';
import { db, insert, update } from './helpers.ts';
import { normalized } from './adminPolicy.ts';

export const profileFields = ['email', 'comment', 'firstName', 'mi', 'lastName', 'company', 'phone', 'fax', 'mobile',
  'secondEmail', 'secondPhone', 'secondMobile', 'secondFax', 'countryId', 'address', 'city', 'stateId', 'zip', 'venSAP'];

export function editableProfile(input: Record<string, unknown>): Record<string, unknown> {
  const lower = Object.fromEntries(Object.entries(input).map(([key, value]) => [key.toLowerCase(), value]));
  return Object.fromEntries(profileFields.filter((key) => lower[key.toLowerCase()] !== undefined).map((key) => [key, lower[key.toLowerCase()]]));
}

export const directoryIdentityEligible = (entry: MockRecord) => entry.enabled !== false &&
  entry.isEnabled !== false && entry.isDisabled !== true && entry.disabled !== true &&
  entry.isEligible !== false && entry.eligible !== false && entry.isLockedOut !== true &&
  entry.hidden !== true && entry.hideFromAddressLists !== true;
const identityAliases = (record: Record<string, unknown>) => [record.userName, record.email, record.networkId,
  record.directoryVerifiedEmail, ...(Array.isArray(record.aliases) ? record.aliases : []),
  ...(Array.isArray(record.directoryAliases) ? record.directoryAliases : [])].map(normalized).filter(Boolean);
export function directorySuggestions(query: unknown): Record<string, unknown>[] {
  const email = normalized(query);
  if (email.length < 2 || email.length > 256) return [];
  const seen = new Set<string>();
  return (db().directory ?? [])
    .filter((entry) => directoryIdentityEligible(entry) && normalized(entry.email).includes(email) &&
      !seen.has(normalized(entry.email)) && seen.add(normalized(entry.email)))
    .sort((left, right) => normalized(left.email).localeCompare(normalized(right.email)))
    .slice(0, 10)
    .map((entry) => {
      const profile = directoryProfile(entry);
      const existing = registeredMatch(profile);
      return { ...profile, ...(existing ? { existingUserId: existing.userId ?? existing.oid } : {}) };
    });
}

export function directoryMember(input: Record<string, unknown>): MockRecord {
  const values = Object.fromEntries(Object.entries(input).map(([key, value]) => [key.toLowerCase(), value]));
  const email = normalized(values.email);
  if (!email) throw new Error('An exact email address is required for synthetic directory lookup.');
  if (!Array.isArray(db().directory)) throw new Error('The synthetic directory is unavailable. Try verification again later.');
  const matches = (db().directory ?? []).filter((row) => normalized(row.email) === email);
  if (!matches.length) throw new Error('No synthetic directory member matches the supplied email.');
  if (matches.length !== 1) throw new Error('The synthetic directory email match is ambiguous.');
  const entry = matches[0];
  if (!directoryIdentityEligible(entry)) throw new Error('This synthetic directory member is disabled or ineligible.');
  return entry;
}

export function directoryProfile(entry: MockRecord): Record<string, unknown> {
  return { email: String(entry.email ?? '').trim(), firstName: entry.firstName, mi: entry.mi,
    lastName: entry.lastName, company: entry.company, userName: entry.userName, networkId: entry.networkId,
    fullName: `${entry.firstName ?? ''} ${entry.lastName ?? ''}`.trim(),
    directoryIdentityId: entry.oid, directoryVerifiedEmail: String(entry.email ?? '').trim(),
    directoryVerifiedOn: new Date().toISOString(), directorySource: 'SYNTHETIC', directoryEligible: directoryIdentityEligible(entry),
    directoryAliases: [...new Set(identityAliases(entry))] };
}

export function registeredMatch(profile: Record<string, unknown>, exceptId?: string): MockRecord | undefined {
  const identities = new Set(identityAliases(profile));
  return db().users.find((user) => user.oid !== exceptId &&
    ((profile.directoryIdentityId && user.directoryIdentityId === profile.directoryIdentityId) ||
      identityAliases(user).some((value) => identities.has(value))));
}

export function assertUnregistered(profile: Record<string, unknown>, exceptId?: string): void {
  if (registeredMatch(profile, exceptId)) throw new Error('A user with this user name or email is already registered.');
}

export function assertNoPendingRequest(profile: Record<string, unknown>): void {
  const identities = new Set(identityAliases(profile));
  if (db().userRequests.some((request) => [1, 16].includes(Number(request.status)) &&
    ((profile.directoryIdentityId && request.directoryIdentityId === profile.directoryIdentityId) ||
      identityAliases(request).some((value) => identities.has(value))))) throw new Error('An access request is already pending.');
}

export function booleanInput(value: unknown, fallback: boolean): boolean {
  if (value === undefined) return fallback;
  if (typeof value !== 'boolean') throw new Error('Approval and unlock flags must be boolean values.');
  return value;
}

export function internalNotesPatch(input: Record<string, unknown>, user: MockRecord): Record<string, unknown> {
  const fields = Object.fromEntries(Object.entries(input).map(([key, value]) => [key.toLowerCase(), value]));
  const saved = Object.fromEntries(Object.entries(user).map(([key, value]) => [key.toLowerCase(), value]));
  const readonly = new Set([...profileFields.filter((key) => key !== 'comment'), 'userName', 'networkId', 'fullName',
    'directoryIdentityId', 'directoryVerifiedEmail', 'directoryVerifiedOn', 'directorySource', 'directoryEligible', 'directoryAliases',
    'psl', 'pslName', 'pslId', 'pslNames', 'profilePslIds'].map((key) => key.toLowerCase()));
  for (const [key, value] of Object.entries(fields)) {
    if (key === 'comment' || key === 'notes') {
      if (typeof value !== 'string') throw new Error('Application notes must be a string.');
    } else if (!readonly.has(key) || JSON.stringify(value) !== JSON.stringify(saved[key])) {
      throw new Error('Internal directory identity, contacts, address and profile PSLs are read-only. Save application notes with comment only; use role assignments for access.');
    }
  }
  if (fields.comment !== undefined && fields.notes !== undefined && fields.comment !== fields.notes) throw new Error('Conflicting application notes.');
  return fields.comment !== undefined || fields.notes !== undefined ? { comment: fields.comment ?? fields.notes } : {};
}

export function provisionInternal(entry: MockRecord, isApproved: boolean): MockRecord {
  const profile = directoryProfile(entry);
  assertUnregistered(profile);
  const user = insert('users', { ...profile, portal: 'QCDOC', isInternal: true,
    assignmentScopeVersion: 2, isMockTestAccount: true,
    roles: [], roleAssignments: [], isApproved, isActive: isApproved, isLockedOut: false, isOnline: false,
    packaging: { read: false, write: false, admin: false }, creationDate: new Date().toISOString() });
  return update('users', user.oid, { userId: user.oid, providerUserKey: user.oid });
}

export function submitInternalRequest(input: Record<string, unknown>) {
  const profile = directoryProfile(directoryMember(input));
  assertUnregistered(profile);
  assertNoPendingRequest(profile);
  const record = insert('userRequests', { ...profile, notes: String(input.notes ?? input.Notes ?? ''),
    portal: 'QCDOC', status: 16, statusId: 16, statusName: 'Created', reqStatusName: 'Created', createdBy: profile.userName });
  return { success: true, isApproved: false, userId: record.oid, userName: profile.userName, message: 'Access request saved for local administrator approval.' };
}